/**
 * Страница профиля пациента.
 * Позволяет просматривать и редактировать медицинские данные.
 */

import React, { useEffect, useState } from 'react';
import { patientAPI, authAPI } from '../../api/client';
import { Patient } from '../../types';

const ProfilePage: React.FC = () => {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    birth_date: '',
    diagnosis_date: '',
    ms_type: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    current: '', newPass: '', confirm: '',
  });
  const [passwordMsg, setPasswordMsg] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await patientAPI.getProfile();
        const p = res.data.patient;
        setPatient(p);
        setForm({
          birth_date: p.birth_date || '',
          diagnosis_date: p.diagnosis_date || '',
          ms_type: p.ms_type || '',
        });
      } catch (err) {
        console.error('Ошибка загрузки профиля:', err);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await patientAPI.updateProfile(form);
      setPatient(res.data.patient);
      setEditing(false);
      setMessage('Профиль обновлён');
    } catch (err: any) {
      setMessage(err.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    setPasswordMsg('');
    if (passwordForm.newPass !== passwordForm.confirm) {
      setPasswordMsg('Новый пароль и подтверждение не совпадают');
      return;
    }
    try {
      await authAPI.changePassword(passwordForm.current, passwordForm.newPass);
      setPasswordMsg('Пароль успешно изменён');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    } catch (err: any) {
      setPasswordMsg(err.response?.data?.error || 'Ошибка смены пароля');
    }
  };

  if (!patient) return <div style={{ padding: 40 }}>Загрузка...</div>;

  return (
    <div>
      <h1 style={{ marginBottom: 24 }}>Мой профиль</h1>

      <div style={styles.card}>
        {message && (
          <div style={{
            ...styles.message,
            backgroundColor: message.includes('обновлён') ? '#f0fdf4' : '#fef2f2',
            color: message.includes('обновлён') ? '#166534' : '#dc2626',
          }}>
            {message}
          </div>
        )}

        <div style={styles.row}>
          <span style={styles.label}>Имя:</span>
          <span>{patient.first_name} {patient.last_name}</span>
        </div>
        <div style={styles.row}>
          <span style={styles.label}>Email:</span>
          <span>{patient.email}</span>
        </div>

        <div style={styles.row}>
          <span style={styles.label}>Дата рождения:</span>
          {editing ? (
            <input
              type="date" value={form.birth_date}
              onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
              style={styles.input}
            />
          ) : (
            <span>{patient.birth_date || 'Не указана'}</span>
          )}
        </div>

        <div style={styles.row}>
          <span style={styles.label}>Дата диагноза:</span>
          {editing ? (
            <input
              type="date" value={form.diagnosis_date}
              onChange={(e) => setForm({ ...form, diagnosis_date: e.target.value })}
              style={styles.input}
            />
          ) : (
            <span>{patient.diagnosis_date || 'Не указана'}</span>
          )}
        </div>

        <div style={styles.row}>
          <span style={styles.label}>Тип РС:</span>
          {editing ? (
            <select
              value={form.ms_type}
              onChange={(e) => setForm({ ...form, ms_type: e.target.value })}
              style={styles.input}
            >
              <option value="">Не указан</option>
              <option value="RRMS">RRMS (ремиттирующий)</option>
              <option value="PPMS">PPMS (первично-прогрессирующий)</option>
              <option value="SPMS">SPMS (вторично-прогрессирующий)</option>
              <option value="PRMS">PRMS (прогрессивно-рецидивирующий)</option>
            </select>
          ) : (
            <span>{patient.ms_type || 'Не указан'}</span>
          )}
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
          {editing ? (
            <>
              <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button style={styles.cancelBtn} onClick={() => setEditing(false)}>
                Отмена
              </button>
            </>
          ) : (
            <button style={styles.editBtn} onClick={() => setEditing(true)}>
              Редактировать
            </button>
          )}
        </div>
      </div>

      {/* Смена пароля */}
      <div style={{ ...styles.card, marginTop: 20 }}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>Смена пароля</h3>
        {passwordMsg && (
          <div style={{
            ...styles.message,
            backgroundColor: passwordMsg.includes('изменён') ? '#f0fdf4' : '#fef2f2',
            color: passwordMsg.includes('изменён') ? '#166534' : '#dc2626',
          }}>
            {passwordMsg}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            type="password" placeholder="Текущий пароль"
            value={passwordForm.current}
            onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
            style={styles.input}
          />
          <input
            type="password" placeholder="Новый пароль (мин. 8, A-Z, a-z, 0-9)"
            value={passwordForm.newPass}
            onChange={(e) => setPasswordForm({ ...passwordForm, newPass: e.target.value })}
            style={styles.input}
          />
          <input
            type="password" placeholder="Подтвердите новый пароль"
            value={passwordForm.confirm}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
            style={styles.input}
          />
          <button style={styles.editBtn} onClick={handlePasswordChange}>
            Сменить пароль
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: 'var(--bg-card)', padding: 24, borderRadius: 12,
    boxShadow: 'var(--shadow-card)', maxWidth: 500,
  },
  message: {
    padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14,
  },
  row: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '10px 0', borderBottom: '1px solid var(--border-light)', fontSize: 15,
  },
  label: { fontWeight: 600, minWidth: 150, color: 'var(--text-primary)' },
  input: {
    padding: '6px 12px', border: '1px solid var(--border-color)',
    borderRadius: 8, fontSize: 14,
  },
  editBtn: {
    backgroundColor: '#2563eb', color: '#fff', border: 'none',
    padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
  saveBtn: {
    backgroundColor: '#16a34a', color: '#fff', border: 'none',
    padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
  cancelBtn: {
    backgroundColor: '#6b7280', color: '#fff', border: 'none',
    padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
    fontSize: 14, fontWeight: 600,
  },
};

export default ProfilePage;
